import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  CalendarCheck, MoreHorizontal, Trash2,
  Calendar as CalendarIcon, ListChecks, Grid3x3, Table as TableIcon,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageSkeleton } from "@/components/LoadingSkeleton";
import {
  MonthView, WeekView, MatrixTableView, TemplateMatrix, FilterBar, ActivityDialog,
  LegendRow,
} from "@/components/publishing/views";


export default function PublishingSchedule() {
  const { listId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [templateRows, setTemplateRows] = useState([]);
  const [calendar, setCalendar] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [tab, setTab] = useState("template");
  // Dialog showing article activity for a clicked slot.
  const [activitySlot, setActivitySlot] = useState(null);
  const [filter, setFilter] = useState("none");

  // Refetch calendar and pick fresh slot data when activity dialog opens — this fixes
  // stale enrichment fields (writer/publisher/keyword/post_slug) after user edits.
  const openActivity = useCallback(async (slot) => {
    try {
      const c = await api.get(`/publishing-schedules/${listId}/calendar`);
      setCalendar(c.data);
      const fresh = (c.data.slots || []).find(
        (s) => s.date === slot.date && s.project_id === slot.project_id && s.status === slot.status
      );
      setActivitySlot(fresh || slot);
    } catch {
      setActivitySlot(slot);
    }
  }, [listId]);

  const canEdit = user && ["super_admin", "admin", "department_admin", "content_manager", "team_lead"].includes(user.role);
  // Iter 37 — Publishing schedules are Content-workflow tools; writers must
  // not access them. Publishing TL / Content Team Lead / Publisher / CM /
  // DA / Admin / SA are allowed. We rely on the backend's `_sidebar_can_view`
  // to enforce this centrally (returns 403 → we redirect gracefully).
  const canView = user && (
    ["super_admin", "admin", "department_admin", "content_manager", "team_lead"].includes(user.role)
    || (user.positions || []).some((p) => (p.position || "").toLowerCase().trim() === "publisher")
  );

  const load = useCallback(async () => {
    try {
      const [l, t, c] = await Promise.all([
        api.get(`/sidebar-lists/${listId}`),
        api.get(`/publishing-schedules/${listId}/template`),
        api.get(`/publishing-schedules/${listId}/calendar`),
      ]);
      if (l.data.kind !== "publishing_schedule") { nav(`/lists/${listId}`); return; }
      // Iter 37 — publishing schedules are hidden from writers/editors.
      // If the server allowed us here but our client role check disagrees,
      // redirect home rather than showing an empty page.
      if (!canView) { toast.error("You don't have access to Publishing Schedules"); nav("/dash"); return; }
      setList(l.data);
      setTemplateRows(t.data.rows || []);
      setCalendar(c.data);
    } catch (e) { toast.error(formatApiError(e)); nav("/dash"); }
  }, [listId, nav]);
  useEffect(() => { load(); }, [load]);

  // Refetch calendar every time the user switches to a calendar tab so status
  // changes made in the project spreadsheet immediately reflect.
  useEffect(() => {
    if (!listId) return;
    if (tab === "template") return;
    (async () => {
      try {
        const c = await api.get(`/publishing-schedules/${listId}/calendar`);
        setCalendar(c.data);
      } catch (e) { /* ignore transient errors on tab switch */ }
    })();
  }, [tab, listId]);

  const toggleCell = async (projectId, day, next) => {
    setTemplateRows((rows) => rows.map((r) => r.project_id === projectId ? { ...r, [day]: next } : r));
    try {
      await api.patch(`/publishing-schedules/${listId}/template`, {
        updates: [{ project_id: projectId, day, checked: next }],
      });
      // Re-fetch calendar so red/green/yellow slots reflect the change.
      const c = await api.get(`/publishing-schedules/${listId}/calendar`);
      setCalendar(c.data);
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  // Iter 45.45 — set an exact publish COUNT for a project+day (driven by
  // the +/− controls in TemplateMatrix). Optimistic, then refetch the
  // calendar so slot expectations update immediately.
  const setCount = async (projectId, day, count) => {
    setTemplateRows((rows) => rows.map((r) => r.project_id === projectId ? { ...r, [day]: count } : r));
    try {
      await api.patch(`/publishing-schedules/${listId}/template`, {
        updates: [{ project_id: projectId, day, count }],
      });
      const c = await api.get(`/publishing-schedules/${listId}/calendar`);
      setCalendar(c.data);
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  const saveListName = async () => {
    const name = nameDraft.trim();
    if (!name || name === list.name) { setEditingName(false); return; }
    try { const r = await api.patch(`/sidebar-lists/${listId}`, { name }); setList(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
    setEditingName(false);
  };
  const deleteList = async () => {
    if (!window.confirm(`Delete "${list.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/sidebar-lists/${listId}`);
      toast.success("Deleted");
      window.dispatchEvent(new Event("sidebar-lists-changed"));
      nav("/dash");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!list) return <PageSkeleton title cards={0} rows={8} filters />;

  // Apply quick filter to the calendar slots before passing to views.
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const weekOff = (now.getDay() + 6) % 7;
  const weekStart = new Date(now); weekStart.setDate(now.getDate() - weekOff); weekStart.setHours(0,0,0,0);
  const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6); weekEnd.setHours(23,59,59,999);
  const filterSlot = (s) => {
    if (filter === "none") return true;
    if (s.status !== "red") return false;
    const d = new Date(s.date + "T00:00:00");
    if (filter === "missed_m") return d >= monthStart && d <= monthEnd;
    if (filter === "missed_w") return d >= weekStart && d <= weekEnd;
    return true;
  };
  const filteredSlots = (calendar?.slots || []).filter(filterSlot);

  return (
    <div className="min-h-full bg-slate-50">
      <div className="border-b border-slate-200 bg-white px-6 py-3 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3 min-w-0">
          <CalendarCheck className="h-5 w-5 text-emerald-500" />
          {editingName ? (
            <Input autoFocus value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} onBlur={saveListName}
              onKeyDown={(e) => { if (e.key === "Enter") saveListName(); if (e.key === "Escape") setEditingName(false); }}
              className="h-8 max-w-md" />
          ) : (
            <button onClick={() => { if (canEdit) { setNameDraft(list.name); setEditingName(true); } }}
              className="text-xl font-semibold text-slate-900 hover:text-emerald-600 transition-colors truncate max-w-md text-left">
              {list.name}
            </button>
          )}
          <span className="text-[10px] uppercase tracking-wider text-emerald-700 px-2 py-0.5 rounded bg-emerald-100">
            Publishing Schedule
          </span>
        </div>
        {canEdit && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem className="text-red-600" onClick={deleteList}>
                <Trash2 className="h-4 w-4 mr-2" /> Delete schedule
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <div className="p-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border border-slate-200 mb-4">
            <TabsTrigger value="template" data-testid="tab-template"><ListChecks className="h-4 w-4 mr-1.5" /> Template</TabsTrigger>
            <TabsTrigger value="month" data-testid="tab-month"><CalendarIcon className="h-4 w-4 mr-1.5" /> Month</TabsTrigger>
            <TabsTrigger value="week" data-testid="tab-week"><Grid3x3 className="h-4 w-4 mr-1.5" /> Week</TabsTrigger>
            <TabsTrigger value="table" data-testid="tab-table"><TableIcon className="h-4 w-4 mr-1.5" /> Table</TabsTrigger>
          </TabsList>

          <TabsContent value="template">
            <TemplateMatrix rows={templateRows} canEdit={canEdit} onToggle={toggleCell} onSetCount={setCount} />
          </TabsContent>
          <TabsContent value="month"><FilterBar filter={filter} setFilter={setFilter} /><MonthView slots={filteredSlots} onSlotClick={openActivity} /></TabsContent>
          <TabsContent value="week"><FilterBar filter={filter} setFilter={setFilter} /><WeekView slots={filteredSlots} onSlotClick={openActivity} /></TabsContent>
          <TabsContent value="table"><FilterBar filter={filter} setFilter={setFilter} /><MatrixTableView slots={filteredSlots} onSlotClick={openActivity} /></TabsContent>
        </Tabs>

        <LegendRow />
      </div>

      {activitySlot && (
        <ActivityDialog slot={activitySlot} onClose={() => setActivitySlot(null)} />
      )}
    </div>
  );
}

